import { c as _c } from "react/compiler-runtime";
import Link from '@/components/ui/Link';
import { PROVIDER_ICONS } from './DynamicSocialShare';
import { AuthorCardItem, BreadcrumbItem, HeroField } from '@/lib/types/articleTemplate';
import { resolveAuthorItems, resolveAuthorCard, ResolvedAuthorCard } from '@/lib/articleTemplateFields';
export interface AuthorTrailProps {
  /** Simple/legacy mode — the standalone ArticleAuthorBlock component type
   *  (name + link list only). Ignored whenever `field.author` is set. */
  items?: BreadcrumbItem[];
  /** Rich-card mode — pass the full authorBlock HeroField so profileShape/
   *  authorLayout/cardLayout/guestAuthor are available. When `field.author`
   *  is unset, falls back to `field.authorItems` (or the plain `items` prop). */
  field?: HeroField;
  story: Record<string, unknown> | null | undefined;
}
function AuthorSocialIcons(t0) {
  const $ = _c(4);
  const {
    links
  } = t0;
  if (links.length === 0) {
    return null;
  }
  let t1;
  if ($[0] !== links) {
    t1 = links.map(_temp);
    $[0] = links;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <span className="flex items-center gap-1">{t1}</span>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp(link) {
  const Icon = PROVIDER_ICONS[(link.reactIconKey || link.id).toLowerCase()];
  return <a key={link.id} href={link.url || "#"} target="_blank" rel="noopener noreferrer" aria-label={link.label} title={link.label} className="w-5 h-5 rounded-full flex items-center justify-center text-FFFFFF overflow-hidden flex-shrink-0" style={{
    backgroundColor: link.iconType === "image" ? "transparent" : link.color || "#64748b"
  }}>{Icon ? <Icon size={11} /> : link.iconType === "image" && link.iconImageUrl ? <img src={link.iconImageUrl} alt={link.label} className="w-full h-full object-cover" loading="lazy" decoding="async" /> : <span className="text-[10px]">{link.icon}</span>}</a>;
}
function AuthorCard(t0) {
  const $ = _c(30);
  const {
    card,
    profileShape,
    cardLayout
  } = t0;
  const shapeClass = profileShape === "square" ? "rounded-none" : "rounded-full";
  const layoutClass = cardLayout === "stacked" ? "flex-col items-start gap-1" : "items-center gap-2";
  const t1 = `flex ${layoutClass}`;
  let t2;
  if ($[0] !== card.labelStyle?.align || $[1] !== card.labelStyle?.color || $[2] !== card.labelStyle?.fontSize || $[3] !== card.labelStyle?.fontWeight || $[4] !== card.prefixLabel) {
    t2 = card.prefixLabel && <span className="text-14-tamil-500 text-808080" style={{
      color: card.labelStyle?.color,
      fontSize: card.labelStyle?.fontSize,
      fontWeight: card.labelStyle?.fontWeight,
      textAlign: card.labelStyle?.align
    }}>{card.prefixLabel}</span>;
    $[0] = card.labelStyle?.align;
    $[1] = card.labelStyle?.color;
    $[2] = card.labelStyle?.fontSize;
    $[3] = card.labelStyle?.fontWeight;
    $[4] = card.prefixLabel;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== card.label || $[7] !== card.profileImage || $[8] !== card.showProfile || $[9] !== shapeClass) {
    t3 = card.showProfile && (card.profileImage ? <img src={card.profileImage} alt={card.label} className={`w-9 h-9 object-cover flex-shrink-0 ${shapeClass}`} loading="lazy" decoding="async" /> : <span className={`w-9 h-9 flex items-center justify-center bg-EDEDED text-4D4D4D text-xs flex-shrink-0 ${shapeClass}`}>👤</span>);
    $[6] = card.label;
    $[7] = card.profileImage;
    $[8] = card.showProfile;
    $[9] = shapeClass;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== card.label || $[12] !== card.nameStyle?.align || $[13] !== card.nameStyle?.color || $[14] !== card.nameStyle?.fontSize || $[15] !== card.nameStyle?.fontWeight || $[16] !== card.showName || $[17] !== card.url) {
    t4 = card.showName && <Link href={card.url || "#"} className="text-14-tamil-600 sm:text-18-tamil-600 text-1B1B1B hover:text-012061" style={{
      color: card.nameStyle?.color,
      fontSize: card.nameStyle?.fontSize,
      fontWeight: card.nameStyle?.fontWeight,
      textAlign: card.nameStyle?.align
    }}>{card.label}</Link>;
    $[11] = card.label;
    $[12] = card.nameStyle?.align;
    $[13] = card.nameStyle?.color;
    $[14] = card.nameStyle?.fontSize;
    $[15] = card.nameStyle?.fontWeight;
    $[16] = card.showName;
    $[17] = card.url;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] !== card.showSocial || $[20] !== card.socialLinks) {
    t5 = card.showSocial && <AuthorSocialIcons links={card.socialLinks || []} />;
    $[19] = card.showSocial;
    $[20] = card.socialLinks;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  let t6;
  if ($[22] !== t4 || $[23] !== t5) {
    t6 = <span className="flex items-center gap-2 flex-wrap">{t4}{t5}</span>;
    $[22] = t4;
    $[23] = t5;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  let t7;
  if ($[25] !== t1 || $[26] !== t2 || $[27] !== t3 || $[28] !== t6) {
    t7 = <div className={t1}>{t2}{t3}{t6}</div>;
    $[25] = t1;
    $[26] = t2;
    $[27] = t3;
    $[28] = t6;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  return t7;
}

/**
 * Renders the author block for ArticleHeroSection's `authorBlock` field.
 * Two modes, matching the Builder admin's data model exactly:
 *
 * - Rich card mode (`field.author` set): profile picture, name (linking to
 *   their page), and social icons, for the primary author and — when
 *   `guestAuthorEnabled` and a non-hidden `guestAuthor` are both present —
 *   a second card alongside it. `authorLayout` controls whether the two
 *   cards sit side by side or stacked; `cardLayout`/`profileShape` control
 *   each card's own internal layout/avatar shape.
 * - Simple/legacy mode (no `field.author`): "By Name1, Name2, ..." — used by
 *   the standalone ArticleAuthorBlock component type, which stays
 *   intentionally simple (name + link only, no profile/social).
 */
export default function AuthorTrail(t0) {
  const $ = _c(32);
  const {
    items,
    field,
    story
  } = t0;
  if (field?.author) {
    let t1;
    if ($[0] !== field.author || $[1] !== story) {
      t1 = resolveAuthorCard(field.author, story);
      $[0] = field.author;
      $[1] = story;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    const author = t1;
    let t2;
    if ($[3] !== field.guestAuthor || $[4] !== field.guestAuthorEnabled || $[5] !== story) {
      t2 = field.guestAuthorEnabled ? resolveAuthorCard(field.guestAuthor, story) : null;
      $[3] = field.guestAuthor;
      $[4] = field.guestAuthorEnabled;
      $[5] = story;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    const guest = t2;
    if (!author && !guest) {
      return null;
    }
    const layoutClass = field.authorLayout === "stacked" ? "flex-col items-start gap-3" : "flex-row flex-wrap items-center gap-4";
    const t3 = `flex ${layoutClass} p-3`;
    let t4;
    if ($[7] !== author || $[8] !== field.cardLayout || $[9] !== field.profileShape) {
      t4 = author && <AuthorCard card={author} profileShape={field.profileShape} cardLayout={field.cardLayout} />;
      $[7] = author;
      $[8] = field.cardLayout;
      $[9] = field.profileShape;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    let t5;
    if ($[11] !== field.cardLayout || $[12] !== field.profileShape || $[13] !== guest) {
      t5 = guest && <AuthorCard card={guest} profileShape={field.profileShape} cardLayout={field.cardLayout} />;
      $[11] = field.cardLayout;
      $[12] = field.profileShape;
      $[13] = guest;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    let t6;
    if ($[15] !== t3 || $[16] !== t4 || $[17] !== t5) {
      t6 = <div className={t3}>{t4}{t5}</div>;
      $[15] = t3;
      $[16] = t4;
      $[17] = t5;
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    return t6;
  }
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[19] !== field?.authorItems || $[20] !== items || $[21] !== story) {
    t5 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const resolved = resolveAuthorItems(field?.authorItems ?? items, story);
      if (resolved.length === 0) {
        t5 = null;
        break bb0;
      }
      t1 = "text-14-tamil-500 text-4D4D4D p-3";
      t2 = "By";
      t3 = " ";
      t4 = resolved.map(_temp2);
    }
    $[19] = field?.authorItems;
    $[20] = items;
    $[21] = story;
    $[22] = t1;
    $[23] = t2;
    $[24] = t3;
    $[25] = t4;
    $[26] = t5;
  } else {
    t1 = $[22];
    t2 = $[23];
    t3 = $[24];
    t4 = $[25];
    t5 = $[26];
  }
  if (t5 !== Symbol.for("react.early_return_sentinel")) {
    return t5;
  }
  let t6;
  if ($[27] !== t1 || $[28] !== t2 || $[29] !== t3 || $[30] !== t4) {
    t6 = <p className={t1}>{t2}{t3}{t4}</p>;
    $[27] = t1;
    $[28] = t2;
    $[29] = t3;
    $[30] = t4;
    $[31] = t6;
  } else {
    t6 = $[31];
  }
  return t6;
}
function _temp2(item, idx) {
  return <span key={item.id}>{idx > 0 && ", "}{item.renderMode === "html" ? <span dangerouslySetInnerHTML={{
      __html: item.customHtml || ""
    }} /> : item.renderMode === "image" && item.customImageUrl ? <a href={item.customImageLink || item.url || "#"} className="inline-flex items-center"><img src={item.customImageUrl} alt={item.label} className="h-6 w-auto align-middle" loading="lazy" decoding="async" /></a> : <Link href={item.url || "#"} className="text-14-tamil-600 sm:text-18-tamil-600 text-1B1B1B hover:text-012061">{item.label}</Link>}</span>;
}